import type { TranslateActivityText } from "./reasoningLanguageGuard.ts";

/** Sends several texts in one request; returns one translation per text ("" = failed). */
export type PostActivityTranslations = (texts: string[], signal: AbortSignal) => Promise<string[]>;

type Queued = {
  text: string;
  resolve: (value: string) => void;
  reject: (error: unknown) => void;
};

/**
 * Transport for the reasoning guard. Calls are queued, at most `maxInFlight` requests run
 * at once, and whatever piled up meanwhile goes out together in the next request. The
 * timeout starts when a request is sent, not when its texts were queued. The defaults
 * stay below the server's limits (6 texts, 8000 characters per request).
 */
export function createBatchedActivityTranslator(options: {
  post: PostActivityTranslations;
  maxInFlight?: number;
  maxBatch?: number;
  maxChars?: number;
  timeoutMs?: number;
}): TranslateActivityText {
  const maxInFlight = options.maxInFlight ?? 2;
  const maxBatch = options.maxBatch ?? 6;
  const maxChars = options.maxChars ?? 3_500;
  const timeoutMs = options.timeoutMs ?? 5_000;
  const queue: Queued[] = [];
  let inFlight = 0;
  let scheduled = false;

  const takeBatch = (): Queued[] => {
    const batch: Queued[] = [];
    let chars = 0;
    while (queue.length > 0 && batch.length < maxBatch) {
      const next = queue[0]!;
      if (batch.length > 0 && chars + next.text.length > maxChars) break;
      batch.push(queue.shift()!);
      chars += next.text.length;
    }
    return batch;
  };

  const send = (batch: Queued[]) => {
    inFlight += 1;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    options
      .post(
        batch.map((item) => item.text),
        controller.signal,
      )
      .then(
        (results) => {
          batch.forEach((item, index) => {
            const translated = String(results[index] ?? "").trim();
            if (translated) item.resolve(translated);
            else item.reject(new Error("empty_translation"));
          });
        },
        (error: unknown) => {
          for (const item of batch) item.reject(error);
        },
      )
      .finally(() => {
        clearTimeout(timer);
        inFlight -= 1;
        pump();
      });
  };

  const pump = () => {
    while (inFlight < maxInFlight && queue.length > 0) send(takeBatch());
  };

  return (text) =>
    new Promise<string>((resolve, reject) => {
      queue.push({ text, resolve, reject });
      // Wait one microtask so pieces queued in the same render go out together.
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        pump();
      });
    });
}

export const postActivityTranslations: PostActivityTranslations = async (texts, signal) => {
  const response = await fetch("/api/ai/activity-translate", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ texts }),
    signal,
  });
  if (!response.ok) throw new Error("translation_failed");
  const body = (await response.json()) as { texts?: unknown };
  if (!Array.isArray(body.texts)) throw new Error("translation_failed");
  return body.texts.map((value) => (typeof value === "string" ? value : ""));
};
